import { autoinject, computedFrom, bindable, observable } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import {
	ReportTemplate,
	IReportTemplateAM,
	ComparisonType,
	IGeneratedRow,
	IReportSource,
	IReportTemplateColumn,
	IRowGeneratorParameters,
	IComparisonColumn,
	ReportTemplateRowType,
	IAccountGroupRow,
	IPerformanceIndicatorRow, ReportFinancialPrecision
} from './models/report-template';
import { NewReportPreviewSwapStore, ReportRouteStore } from './report-route-store';
import { ReportService, IReportAM, IReportHeaderCellAM } from './report-api';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { includes } from 'lodash';
import { ScopeService, IScopeAM } from '../scopes/scope-service';
import { ScopeTypeService, ScopeType } from '../scopes/scope-type-service';
import { ResourceService } from '../../utils/resourceService';
import { FileService } from "../../utils/file-service";
import {
	IDraggableHandler,
	IDroppableHandler,
} from "../../components/drag-and-drop/models";
import { IReportRowAM, IReportGroupRowAM } from './report-api';
import { AccountingDimensionService } from '../accounting-dimensions/accounting-dimension-api';

interface IReportEditUrlParams {
	reportId: string;
	customerId: string;
}

@autoinject
export class ReportEdit implements IDroppableHandler {
	report: IReportAM;
	backButtonTitle: string;
	rowGeneratorParametersVisible = false;
	isLoadErroneous: boolean;

	// Drag-and-drop for column reordering
	droppableHandler: IDroppableHandler;
	columnDragHandler: IDraggableHandler;
	draggedColumnIndex: number = null;
	private dragEnterDepthByColumnIndex: { [columnIndex: number]: number } = {};

	customerId: string;
	scopes: IScopeAM[];
	comparisonTypes: ComparisonType[];
	scopeTypes: ScopeType[];
	scopeTypeIdIsMissing = false;
	showDimensionFiltersElementOption: boolean = null;
	showDimensionSeparationOption: boolean = null;
	scopeChangeMappings: { [scopeId: string]: string };
	scopeChangeDialogVisible: boolean = false;

	deleteReportColumn: IReportHeaderCellAM = null;
	deleteColumnConfirmationDialog: HTMLElement;
	deleteColumnConfirmationMessage: string;
	deleteComparisonColumns: IReportHeaderCellAM[];

	measureCodes: string[] = [];

	@observable includeDeletedRows: boolean;

	private template: ReportTemplate;
	private formMode: ReportFormMode;
	private subscription: Subscription;
	private isCreatingNewReport: boolean;

	constructor(
		private routeStore: ReportRouteStore,
		private creationRouteStore: NewReportPreviewSwapStore,
		private api: ReportService,
		private router: Router,
		private eventAggregator: EventAggregator,
		private scopeService: ScopeService,
		private scopeTypeService: ScopeTypeService,
		private resource: ResourceService,
		private accountingDimensionService: AccountingDimensionService,
	) {
		this.report = {} as IReportAM;
		this.scopeChangeMappings = {};

		// Initialize drag-and-drop handlers for column reordering
		this.droppableHandler = this;
		this.columnDragHandler = {
			onDragStart: (event: DragEvent, data: { index: number }) => {
				event.stopPropagation();
				event.dataTransfer.effectAllowed = "move";
				event.dataTransfer.setData(
					"application/json",
					JSON.stringify({ index: data.index }),
				);
				this.draggedColumnIndex = data.index;
			},
			onDragEnd: (event: DragEvent, data: { index: number }) => {
				this.draggedColumnIndex = null;
				this.dragEnterDepthByColumnIndex = {};
				document
					.querySelectorAll(".report-header-cell.drag-enter")
					.forEach((element) => element.classList.remove("drag-enter"));
			},
		};
	}

	async activate(urlParams: IReportEditUrlParams) {

		this.customerId = urlParams.customerId;

		if (urlParams.reportId) {
			this.isCreatingNewReport = false;
			await this.activateEdit(urlParams.reportId);
		} else {
			this.isCreatingNewReport = true;
			await this.activateNew();
		}

		let allScopes = (await this.scopeService.getScopesForCustomer(urlParams.customerId));
		this.scopes = this.generatedRow && this.generatedRow.generator === ReportTemplate.generatorType_servicestructure
			? allScopes.filter(s => s.serviceStructureId === this.generatedRow.parameters.serviceStructureId) : allScopes;
		this.comparisonTypes = (await this.api.getComparisonTypes()).map(typeAM => ComparisonType.fromAM(typeAM));
		this.scopeTypes = await this.scopeTypeService.getScopeTypes();
		this.showDimensionFiltersElementOption = await this.api.hasCustomerReportDimensionFilters(urlParams.customerId);
		this.showDimensionSeparationOption = (await this.accountingDimensionService.getDimensionsForCustomer(urlParams.customerId)).length > 0;
	}

	async activateEdit(reportId: string) {
		this.formMode = ReportFormMode.Edit;
		const templateAM = await this.api.getReportTemplate(reportId);

		// Convert old commonRows to new format
		const generatedRow = templateAM && templateAM.content ? templateAM.content.rows[0] as IGeneratedRow : null;
		if (generatedRow && !generatedRow.parameters.selectedCommonRows) {
			const params = generatedRow.parameters;
			params.selectedCommonRows = params.commonRows.map(cr => ({
				rowTemplate: cr,
				accountGroupSeparation:
					(params.accountGroupSeparationForRevenues && cr.type === ReportTemplateRowType.AccountGroup && (<IAccountGroupRow>cr).code === "TUOTOT") ||
					(params.accountGroupSeparationForRevenues && cr.type === ReportTemplateRowType.PerformanceIndicator && (<IPerformanceIndicatorRow>cr).code === "T_TOIMKULUT"),
				accountSeparation: false,
				typeSeparation: false,
			}));
			params.commonRows = undefined;
		}

		this.template = ReportTemplate.fromAM(templateAM);

		const report = this.routeStore.fromCache<IReportAM>(reportId);
		this.report = report || await this.api.getReportForCustomer(reportId, this.customerId, false, e => { this.isLoadErroneous = true; });
		this.measureCodes = this.extractMeasureCodes(this.report);
	}

	async activateNew() {
		this.formMode = ReportFormMode.New;
		this.template = this.creationRouteStore.fromCache<ReportTemplate>();

		if (!this.template) {
			this.router.navigateToRoute('new-report');
			return;
		}

		this.template.isPrivate = true;
		this.report = await this.api.getCalculatedReportForTemplate(this.customerId, this.template.content);
		this.measureCodes = this.extractMeasureCodes(this.report);
	}

	created() {
		this.subscription = this.eventAggregator.subscribe('router:navigation:success', result => {
			this.backButtonTitle = this.router.currentInstruction.config.settings.backButtonResource;
		});
	}

	async addTemplate(editedTemplate: IEditedTemplate) {
		this.template.name = editedTemplate.name;
		this.template.isPrivate = editedTemplate.isPrivate;
		const response = await this.api.addReportTemplate(this.customerId, this.template);

		if (!response.ok) { return; }

		const result: IReportTemplateAM = await response.json();
		this.routeStore.dispose();
		this.router.navigateToRoute('report-entry', { reportId: result.id, customerId: this.customerId });
	}

	async updateTemplate() {
		await this.api.updateReportTemplate(this.template);
		this.router.navigateToRoute('report-entry', { reportId: this.template.id, customerId: this.customerId });
	}

	async saveTemplateAsNew(editedTemplate: IEditedTemplate) {
		const newTemplate = new ReportTemplate({
			name: editedTemplate.name,
			isPrivate: editedTemplate.isPrivate,
			content: this.template.content,
			commentAtBeginning: this.template.commentAtBeginning,
			commentAtEnd: this.template.commentAtEnd,
		});
		const response = await this.api.addReportTemplate(this.customerId, newTemplate);

		if (!response.ok) {
			return;
		}

		const result: ReportTemplate = await response.json();
		this.router.navigateToRoute('report-entry', { reportId: result.id, customerId: this.customerId });
	}

	updateTemplateName(name: string) {
		this.api.updateReportTemplateName(this.template.id, name);
	}

	detached() {
		this.subscription.dispose();
	}

	goBack() {
		this.router.navigateBack();
	}

	get generatedRow(): IGeneratedRow {
		return this.template && this.template.content ? this.template.content.rows[0] as IGeneratedRow : null;
	}

	@computedFrom('scopes', 'template.content.columns')
	get reportScopes(): IScopeAM[] {
		return this.scopes.filter(s => this.template.content && this.template.content.columns.some(c => c.scope === s.id));
	}

	@computedFrom('generatedRow')
	get allowOrganizationUnitsInColumnSettings(): boolean {
		return this.generatedRow.generator === 'ServiceStructure';
	}

	@computedFrom('generatedRow', 'generatedRow.parameters')
	get generatorParameters(): IRowGeneratorParameters {
		if (this.generatedRow == null || this.generatedRow.parameters == null) { return null; }

		const clonedParams = Object.assign({}, this.generatedRow.parameters);
		clonedParams.selectedCommonRows = this.generatedRow.parameters.selectedCommonRows.slice();

		return clonedParams;
	}

	@computedFrom("template.content.source.filters.financialPrecision")
	get financialPrecision(): ReportFinancialPrecision {
		return this.template.content.source.filters.financialPrecision;
	}

	handleScopeChange(reportScope: IScopeAM, newScopeId: string) {
		this.scopeChangeMappings[reportScope.id] = newScopeId;
	}

	updateNewScopesToReportTemplate() {

		this.template.content.columns.map(c => {
			const newScopeId = this.scopeChangeMappings[c.scope];
			if (newScopeId) {
				c.scope = newScopeId;
			}
		});

		this.scopeChangeDialogVisible = false;
		this.refreshReport();
	}

	getScopesExcept(excludeId: string) {
		return this.scopes.filter(s => s.id !== excludeId);
	}

	 storeChangedTemplateOfNewReport() {
		if (this.isCreatingNewReport) {
			this.creationRouteStore.store = this.template;
		}
	}

	updateIdentifiers(identifiers: string[]) {
		this.generatedRow.parameters.identifiers = identifiers;
		this.storeChangedTemplateOfNewReport();
	}
	
	updateFinancialPrecision(precision: ReportFinancialPrecision) {
		this.template.content.source.filters.financialPrecision = precision;
		this.storeChangedTemplateOfNewReport();
	}
	
	updateIncludes(editedParameters: IRowGeneratorParameters) {

		// Don't handle identifiers here, they are not edited with includes and might overwrite changes made elsewhere
		const { identifiers, ...parametersToHandle } = editedParameters;
		Object.assign(this.generatedRow.parameters, parametersToHandle);
		this.storeChangedTemplateOfNewReport();
	}

	updateSource(source: IReportSource) {
		this.template.content.source = source;
		this.storeChangedTemplateOfNewReport();
	}

	async addColumn(index: number, column: IReportTemplateColumn) {
		this.template.content.columns = this.insertColumn(this.template.content.columns, index, column);
		await this.refreshReport();
	}

	private insertColumn(columns: IReportTemplateColumn[], index: number, column: IReportTemplateColumn) {
		return [...columns.slice(0, index), column, ...columns.slice(index)];
	}

	async refreshReport() {
		this.scopeTypeIdIsMissing = false;

		const validateParameters = (params: IRowGeneratorParameters) =>
			(params.includeMeasures || params.includePerformanceIndicators) && !params.scopeTypeId;

		if (validateParameters((this.template.content.rows[0] as IGeneratedRow).parameters)) {
			this.scopeTypeIdIsMissing = true;
			return false;
		}

		this.report = null;
		this.report = await this.api.getCalculatedReportForTemplate(
			this.customerId,
			this.template.content
		);
		this.measureCodes = this.extractMeasureCodes(this.report);

		return true;
	}

	getAddColumnDirection(index: number, columnCount: number) {
		if (index > columnCount - 4) {
			return 'left';
		}

		return 'right';
	}

	private removeCells(row: any, index: number) {
		if (row.rows) {
			row.rows.forEach(r => this.removeCells(r, index));
		}
		if (row.cells) {
			row.cells.splice(index, 1);
		}
	}

	private deleteColumn(column: IReportHeaderCellAM) {
		const index = this.report.headerCells.indexOf(column);
		this.report.headerCells.splice(index, 1);
		this.report.rows.forEach(r => this.removeCells(r, index));
		this.template.content.columns.splice(index, 1);
	}

	private cancelColumnDelete = () => {
		this.deleteReportColumn = null;
		document
			.querySelector('.card-contents')
			.removeEventListener('scroll', this.cancelColumnDelete);
	};

	confirmDeleteColumn(event: any, headerCell: IReportHeaderCellAM) {
		// Resolve confirmation dialog position
		const rect = event.target.parentElement.parentElement // i.glyphicon // span // div.report-header-cell
			.getBoundingClientRect();

		this.deleteColumnConfirmationDialog.style.top = `${rect.height}px`;
		this.deleteColumnConfirmationDialog.style.left = `${rect.left - 15 + rect.width / 2}px`;

		const templateColumns = this.template.content.columns.filter(c => c.type === 'Comparison' && includes((<IComparisonColumn>c).columnIds, headerCell.columnId));

		this.deleteComparisonColumns = this.report.headerCells.filter(cell => {
			return cell !== headerCell && cell.type === 'Comparison' && includes(templateColumns.map(tc => tc.id), cell.columnId);
		});

		if (this.deleteComparisonColumns.length) {
			this.deleteColumnConfirmationMessage = this.resource
				.getResource('Reports_Edit_Label_ConfirmComparisonColumnDelete', [this.deleteComparisonColumns.map(c => c.title).join(', ')]);
		} else {
			this.deleteColumnConfirmationMessage = this.resource
				.getResource('Reports_Edit_Label_ConfirmDataColumnDelete', [headerCell.title]);
		}

		document
			.querySelector('.card-contents')
			.addEventListener('scroll', this.cancelColumnDelete);

		this.deleteReportColumn = headerCell;
	}

	// Column editing methods
	async updateColumnTitle(index: number, title: string): Promise<void> {
		if (index < 0 || index >= this.template.content.columns.length) {
			return;
		}

		// Update the template column
		const column = this.template.content.columns[index];
		column.title = title || null;

		// Update the header cell title immediately for display
		this.report.headerCells[index].title =
			title || this.report.headerCells[index].title;

		this.storeChangedTemplateOfNewReport();
	}

	onColumnDelete(column: IReportHeaderCellAM) {
		this.deleteComparisonColumns.forEach(cc => this.deleteColumn(cc));
		this.deleteColumn(column);
		this.deleteReportColumn = null;
	}

	async includeDeletedRowsChanged() {
		if (this.formMode === ReportFormMode.New) { 
			return; 
		}

		this.report = await this.api.getReportForCustomer(
			this.template.id,
			this.customerId,
			this.includeDeletedRows,
			(e) => {
				this.isLoadErroneous = true;
			},
		);
	}

	// IDroppableHandler implementation for column reordering
	onDragEnter(event: any, targetIndex: number): void {
		if (event.preventDefault) {
			event.preventDefault();
		}
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}

		const targetElement = this.getHeaderDropTargetElement(event);
		if (targetElement) {
			const currentDepth = this.dragEnterDepthByColumnIndex[targetIndex] || 0;
			this.dragEnterDepthByColumnIndex[targetIndex] = currentDepth + 1;
			targetElement.classList.add("drag-enter");
		}
	}

	onDragLeave(event: Event, targetIndex: number): void {
		const targetElement = this.getHeaderDropTargetElement(event);
		if (targetElement) {
			const currentDepth = this.dragEnterDepthByColumnIndex[targetIndex] || 0;
			const nextDepth = Math.max(0, currentDepth - 1);
			this.dragEnterDepthByColumnIndex[targetIndex] = nextDepth;

			if (nextDepth === 0) {
				targetElement.classList.remove("drag-enter");
			}
		}
	}

	onDrop(event: DragEvent, targetIndex: number): void {
		event.stopPropagation();
		event.preventDefault();

		const targetElement = this.getHeaderDropTargetElement(event);
		if (targetElement) {
			this.dragEnterDepthByColumnIndex[targetIndex] = 0;
			targetElement.classList.remove("drag-enter");
		}

		const jsonData = event.dataTransfer.getData("application/json");
		if (!jsonData) {
			return;
		}

		const data = JSON.parse(jsonData);
		const fromIndex = data.index;

		if (fromIndex !== targetIndex && fromIndex !== undefined) {
			this.reorderColumn(fromIndex, targetIndex);
		}
	}

	reorderColumn(fromIndex: number, toIndex: number): void {
		// Reorder template columns (source of truth)
		const [column] = this.template.content.columns.splice(fromIndex, 1);
		this.template.content.columns.splice(toIndex, 0, column);

		// Reorder header cells (display)
		const [headerCell] = this.report.headerCells.splice(fromIndex, 1);
		this.report.headerCells.splice(toIndex, 0, headerCell);

		// Reorder cells in all rows
		this.report.rows.forEach((row) =>
			this.reorderCellsInRow(row, fromIndex, toIndex),
		);

		this.storeChangedTemplateOfNewReport();
	}

	private reorderCellsInRow(
		row: any,
		fromIndex: number,
		toIndex: number,
	): void {
		if (row.rows) {
			row.rows.forEach((r) =>
				this.reorderCellsInRow(r, fromIndex, toIndex),
			);
		}
		if (row.cells) {
			const [cell] = row.cells.splice(fromIndex, 1);
			row.cells.splice(toIndex, 0, cell);
		}
	}

	private getHeaderDropTargetElement(event: Event): Element {
		const currentTarget = event.currentTarget as Element;
		if (currentTarget && currentTarget.classList.contains("report-header-cell")) {
			return currentTarget;
		}

		const eventTarget = event.target as Element;
		return eventTarget ? eventTarget.closest(".report-header-cell") : null;
	}

	private extractMeasureCodes(report: IReportAM): string[] {
		if (!report || !report.rows) { return []; }

		const codes = new Set<string>();
		const codePattern = /Measure_Code:(.+)$/;

		const walk = (rows: IReportRowAM[]) => {
			for (const row of rows) {
				if (row.identifier) {
					const match = codePattern.exec(row.identifier);
					if (match) {
						codes.add(match[1]);
					}
				}
				if ((row as IReportGroupRowAM).rows) {
					walk((row as IReportGroupRowAM).rows);
				}
			}
		};

		walk(report.rows);
		return Array.from(codes).sort((a, b) => a.localeCompare(b));
	}
}

export interface IEditedTemplate {
	name: string;
	isPrivate: boolean;
}

export enum ReportFormMode {
	New = 'new',
	Edit = 'edit'
}
